/**
 * The reply's blocks (what the server sends and accepts) to and from the editor's document.
 *
 *   paragraph  ↔ paragraph, with text, `fieldChip` and `hardBreak` inside
 *   condition  ↔ condition holding two conditionSide nodes ("then", "otherwise") of paragraphs
 *   legal      ↔ legalNote, plain text
 *
 * Empty paragraphs at the top level are dropped on the way out: the server drops them anyway, and
 * keeping them would make a blank line look like an unsaved change.
 */
import type { JSONContent } from '@tiptap/react';
import type { DocumentBlock, InlineItem, ParagraphBlock } from '../api/api';

const paragraphToEditor = (paragraph: ParagraphBlock): JSONContent => {
  const content = paragraph.content
    .map((item): JSONContent | null => {
      if (item.type === 'text') return item.text ? { type: 'text', text: item.text } : null;
      if (item.type === 'field') return { type: 'fieldChip', attrs: { name: item.name } };
      return { type: 'hardBreak' };
    })
    .filter((node): node is JSONContent => node !== null);
  return content.length > 0 ? { type: 'paragraph', content } : { type: 'paragraph' };
};

const sideToEditor = (side: 'then' | 'otherwise', paragraphs: ParagraphBlock[]): JSONContent => ({
  type: 'conditionSide',
  attrs: { side },
  content: paragraphs.length > 0 ? paragraphs.map(paragraphToEditor) : [{ type: 'paragraph' }],
});

export function toEditor(document: DocumentBlock[]): JSONContent {
  const content = document.map((block): JSONContent => {
    if (block.type === 'paragraph') return paragraphToEditor(block);
    if (block.type === 'legal')
      return { type: 'legalNote', content: block.text ? [{ type: 'text', text: block.text }] : [] };
    return {
      type: 'condition',
      attrs: { flag: block.flag },
      content: [sideToEditor('then', block.then), sideToEditor('otherwise', block.otherwise)],
    };
  });
  return { type: 'doc', content: content.length > 0 ? content : [{ type: 'paragraph' }] };
}

const paragraphFromEditor = (node: JSONContent): ParagraphBlock => ({
  type: 'paragraph',
  content: (node.content ?? [])
    .map((child): InlineItem | null => {
      if (child.type === 'text') return { type: 'text', text: child.text ?? '' };
      if (child.type === 'fieldChip')
        return { type: 'field', name: String(child.attrs?.name ?? '') };
      if (child.type === 'hardBreak') return { type: 'break' };
      return null;
    })
    .filter((item): item is InlineItem => item !== null),
});

const isBlank = (paragraph: ParagraphBlock) =>
  paragraph.content.every(
    item => item.type === 'break' || (item.type === 'text' && !item.text.trim()),
  );

const sideFromEditor = (condition: JSONContent, side: 'then' | 'otherwise') =>
  (condition.content ?? [])
    .filter(node => node.attrs?.side === side)
    .flatMap(node => (node.content ?? []).map(paragraphFromEditor))
    .filter(paragraph => !isBlank(paragraph));

export function fromEditor(doc: JSONContent): DocumentBlock[] {
  return (doc.content ?? []).flatMap((node): DocumentBlock[] => {
    if (node.type === 'paragraph') {
      const paragraph = paragraphFromEditor(node);
      return isBlank(paragraph) ? [] : [paragraph];
    }
    if (node.type === 'legalNote')
      return [
        { type: 'legal', text: (node.content ?? []).map(child => child.text ?? '').join('') },
      ];
    if (node.type === 'condition') {
      return [
        {
          type: 'condition',
          flag: String(node.attrs?.flag ?? ''),
          then: sideFromEditor(node, 'then'),
          otherwise: sideFromEditor(node, 'otherwise'),
        },
      ];
    }
    return [];
  });
}
